import React from 'react';
import { useTheme } from '@mui/material/styles';
import { css } from '@emotion/css';

/**
 * Lightweight withStyles adapter that replaces @mui/styles/withStyles.
 * Uses Emotion's css() to generate real CSS class names from style objects,
 * matching the old withStyles API so existing className={classes.xxx} code works.
 *
 * ACHTUNG bei Modifier-Klassen — Emotion schlüsselt Klassen über den
 * REGELINHALT, nicht über den Key-Namen. Zwei Keys mit identischem Inhalt
 * bekommen dieselbe Klasse und damit die Sheet-Position des ERSTEN Vorkommens.
 * Wird so ein Modifier per `classNames(classes.basis, classes.modifier)` über
 * eine Basisklasse gelegt, entscheidet bei gleicher Spezifität die Reihenfolge
 * im Stylesheet — der Modifier kann dann still verlieren.
 *
 * Die Umstellung von hartcodierten Farben auf Theme-Tokens macht das
 * wahrscheinlicher, weil Token-Werte sich naturgemäss wiederholen.
 *
 * Regel für neue Modifier, die nur eine Farbdeklaration enthalten:
 *
 *   modifier: { '&&': { color: theme.palette.error.main } },
 *
 * Das verdoppelt den Selektor (Spezifität 0,2,0 statt 0,1,0) und ändert
 * zugleich den serialisierten Inhalt, wodurch die Hash-Kollision entfällt.
 * `flattenBreakpoints` unten reicht `'&&'` als verschachtelten Selektor durch.
 */

function resolveStyles(stylesObj, theme) {
  const classes = {};
  for (const key in stylesObj) {
    const value = stylesObj[key];
    if (typeof value === 'object' && value !== null) {
      classes[key] = css(flattenBreakpoints(value, theme));
    }
  }
  return classes;
}

function flattenBreakpoints(styleObj, theme) {
  const result = {};
  for (const prop in styleObj) {
    const value = styleObj[prop];
    if (prop.startsWith('@media')) {
      result[prop] = typeof value === 'object' ? flattenBreakpoints(value, theme) : value;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result[prop] = flattenBreakpoints(value, theme);
    } else {
      result[prop] = value;
    }
  }
  return result;
}

export default function withStyles(stylesOrCreator) {
  return function wrapWithStyles(WrappedComponent) {
    const WithStyles = React.forwardRef(function WithStyles(props, ref) {
      const theme = useTheme();
      /**
       * Memoisiert über das Theme. Ohne das lief `resolveStyles` bei jedem
       * Render erneut über das komplette Style-Objekt — projektweit rund 377
       * Keys über 27 Adapter-Nutzer, einzelne Dateien mit über 60. Emotion
       * cacht zwar die erzeugten Klassennamen, die Objekt-Iteration und
       * `flattenBreakpoints` liefen aber jedes Mal.
       *
       * Wichtiger noch: `classes` war bei jedem Render ein neues Objekt und
       * hat damit jede Memoisierung in den Kindkomponenten ausgehebelt.
       *
       * `stylesOrCreator` ist eine Modulkonstante, `theme` wechselt nur beim
       * Moduswechsel — die Abhängigkeit ist damit vollständig.
       */
      const classes = React.useMemo(() => {
        const rawStyles =
          typeof stylesOrCreator === 'function'
            ? stylesOrCreator(theme)
            : stylesOrCreator;
        return resolveStyles(rawStyles, theme);
      }, [theme]);
      // theme mitgeben, damit Klassenkomponenten Farben auch dort auflösen können,
      // wo keine CSS-Klasse greift (SVG-Attribute, Props von Fremdkomponenten).
      // Eine explizit übergebene theme-Prop gewinnt weiterhin.
      return <WrappedComponent ref={ref} theme={theme} {...props} classes={classes} />;
    });

    WithStyles.displayName = `WithStyles(${
      WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`;

    return WithStyles;
  };
}
